import { Author, Figure, Post } from 'codegen/schema'
import { AuthorDetails } from 'components/author-details'
import { Layout } from 'components/layout'
import { RichTextContent } from 'components/rich-text-content'
import { postQuery } from 'lib/queries'
import { getClient } from 'lib/sanity.server'
import { GetStaticProps } from 'next'
import ErrorPage from 'next/error'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { groq } from 'next-sanity'
import { filterDataToSingleItem } from 'utils/sanity/filter-data-to-single-item'
import { getThumbnailSrc } from 'utils/seo/get-thumbnail-src'

import { urlWithSiteOrigin } from '../../utils/url-with-site-origin'
import { AuthorDetailsWrapper } from './ArticlePageView.styles'

/**
 * Makes Next.js aware of all the slugs it can expect at this route
 *
 * See how we've mapped over our found slugs to add a `/` character?
 * Idea: Add these in Sanity and enforce them with validation rules :)
 * https://www.simeongriggs.dev/nextjs-sanity-slug-patterns
 */
export async function getStaticPaths() {
  const allSlugsQuery = groq`*[_type == "post" && defined(slug.current)][].slug.current`
  const pages = await getClient().fetch<string[]>(allSlugsQuery)

  return {
    // paths: pages.map((slug: string) => `/articles/${slug}`),
    paths: pages.map((slug) => ({ params: { slug } })),
    fallback: true,
  }
}

/**
 * Fetch the data from Sanity based on the current slug
 *
 * Important: You _could_ query for just one document, like this:
 * *[slug.current == $slug][0]
 * But that won't return a draft document!
 * And you get a better editing experience
 * fetching draft/preview content server-side
 *
 * Also: Ignore the `preview = false` param!
 * It's set by Next.js "Preview Mode"
 * It does not need to be set or changed here
 */
export const getStaticProps: GetStaticProps = async ({ params, preview = false }) => {
  const query = groq`${postQuery}`
  const queryParams = { slug: params?.slug }
  const data = await getClient(preview).fetch(postQuery, queryParams)

  // Escape hatch, if our query failed to return data
  if (!data || !data.length) return { notFound: true }

  // Helper function to reduce all returned documents down to just one
  const page = filterDataToSingleItem<Post>(data, preview)

  return {
    props: {
      // Pass down the "preview mode" boolean to the client-side
      preview,
      // Pass down the initial content, and our query
      data: { page, query, queryParams },
    },
    revalidate: 60,
  }
}

export interface PageProps {
  preview?: boolean
  data?: {
    page: Post
    query: string
    queryParams: {
      slug: string | string[] | undefined
    }
  }
}

/**
 * The `usePreview` takes care of updating
 * the preview content on the client-side
 */
export const ArticlePageView = ({ data, preview }: PageProps) => {
  const router = useRouter()
  const { asPath, isFallback } = router

  // cast it to unknown first to assign "string" because Post is returning wrote type {_type: "slug", current: string}
  const slug = data?.page?.slug as unknown as string

  if (!isFallback && !slug) {
    return <ErrorPage statusCode={404} />
  }

  if (!data?.page) {
    return <ErrorPage statusCode={404} />
  }

  const page = data.page

  // Notice the optional?.chaining conditionals wrapping every piece of content?
  // This is extremely important as you can't ever rely on a single field
  // of data existing when Editors are creating new documents.
  // It'll be completely blank when they start!

  if (isFallback) {
    return <div>Loading...</div>
  }

  return (
    <Layout
      seoProps={{
        title: page?.title || '',
        description: page?.metaDescription || '',
        datePublished: page.publishedAt,
        dateModified: page._updatedAt,
        canonicalUrl: urlWithSiteOrigin(`/articles/${slug}/`),
        pageUrl: urlWithSiteOrigin(`/articles/${slug}/`),
        thumbnail: getThumbnailSrc(page?.heroImage?.image?.asset._ref as unknown as Figure),
        authorName: ((page?.author as any)?.name ?? 'Unknown Author') as string,
      }}
    >
      {!isFallback && (
        <div>
          {preview && (
            // use html <a> tag if the following Link throws error
            <Link href={`/api/exit-preview?slug=${asPath}`} prefetch={false}>
              Preview Mode Activated!
            </Link>
          )}

          <RichTextContent heroImage={page.heroImage} body={page.body} />
          {page?.tags?.map(({ label, value }) => (
            <button key={value}>{label}</button>
          ))}
          <AuthorDetailsWrapper>
            <AuthorDetails author={page?.author as Author | undefined} />
          </AuthorDetailsWrapper>
        </div>
      )}
    </Layout>
  )
}
